/** Cross-origin session clear via ?loggedOut=1&returnUrl= redirect chain. */

export const ADMIN_SIGNOUT_COOKIE = "he_admin_signout";

export function markAdminSignOutCookie(maxAgeSeconds = 900): void {
  if (typeof document === "undefined") {
    return;
  }
  document.cookie = `${ADMIN_SIGNOUT_COOKIE}=1; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
}

export function consumeAdminSignOutCookie(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  const marked = document.cookie
    .split(";")
    .some((part) => part.trim() === `${ADMIN_SIGNOUT_COOKIE}=1`);
  if (!marked) {
    return false;
  }
  document.cookie = `${ADMIN_SIGNOUT_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  return true;
}

export function clearAdminBrowserStorage(): void {
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
  try {
    localStorage.clear();
  } catch {
    // ignore
  }
}

/**
 * When this SPA is hit as part of the cross-app logout relay (`?loggedOut=1`),
 * wipe storage and continue the chain (or strip the flag on the final landing).
 * Returns true when a full-page redirect was started.
 */
export function handleAdminLoggedOutLandingPage(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.get("loggedOut") !== "1") {
    return false;
  }

  clearAdminBrowserStorage();
  markAdminSignOutCookie();

  const returnUrl = url.searchParams.get("returnUrl")?.trim();
  if (returnUrl) {
    window.location.replace(returnUrl);
    return true;
  }

  url.searchParams.delete("loggedOut");
  const search = url.searchParams.toString();
  const cleaned =
    url.pathname + (search ? `?${search}` : "") + url.hash;
  window.history.replaceState(null, "", cleaned);
  return false;
}

export function relayLoggedOutOrigin(origin: string, nextUrl: string): string {
  const base = origin.replace(/\/+$/, "");
  return `${base}/?loggedOut=1&returnUrl=${encodeURIComponent(nextUrl)}`;
}

export function buildCrossAppRelayReturnUrl(
  relayOrigins: string[],
  finalUrl: string
): string {
  let url = finalUrl;
  for (let i = relayOrigins.length - 1; i >= 0; i--) {
    const origin = relayOrigins[i]?.trim();
    if (!origin) continue;
    url = relayLoggedOutOrigin(origin, url);
  }
  return url;
}

export function buildAuthAppLogoutUrl(options: {
  authAppUrl: string;
  relayOrigins: string[];
  finalReturnUrl: string;
  everywhere?: boolean;
  token?: string;
  refreshToken?: string;
}): string {
  const auth = options.authAppUrl.replace(/\/+$/, "");
  const chained = buildCrossAppRelayReturnUrl(
    options.relayOrigins,
    options.finalReturnUrl
  );
  const url = new URL(`${auth}/logout`);
  url.searchParams.set("returnUrl", chained);
  url.searchParams.set(
    "everywhere",
    options.everywhere === false ? "0" : "1"
  );
  if (options.token) {
    url.searchParams.set("token", options.token);
  }
  if (options.refreshToken) {
    url.searchParams.set("refreshToken", options.refreshToken);
  }
  return url.toString();
}
